import React, { ReactNode } from 'react'
import { useAdminAuth } from '../contexts/AdminAuthContext'
import { Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'

interface ProtectedAdminRouteProps {
  children: ReactNode
}

const ProtectedAdminRoute: React.FC<ProtectedAdminRouteProps> = ({ 
  children
}) => {
  const { user, loading } = useAdminAuth()
  const [timeoutReached, setTimeoutReached] = React.useState(false)

  // Cache local para manter o dashboard visível em refresh
  const ADMIN_USER_KEY = 'aguiar_admin_user'
  const ADMIN_EXP_KEY = 'aguiar_admin_exp'
  const [hasCachedAdmin, setHasCachedAdmin] = React.useState(false)

  React.useEffect(() => {
    try {
      const cached = localStorage.getItem(ADMIN_USER_KEY)
      const expStr = localStorage.getItem(ADMIN_EXP_KEY)
      const exp = expStr ? parseInt(expStr, 10) : undefined
      if (cached && (!exp || Date.now() < exp)) {
        setHasCachedAdmin(true)
        return
      }
    } catch {}
    setHasCachedAdmin(false)
  }, [])

  // Se está carregando mas há cache válido, renderiza o conteúdo imediatamente
  if (loading && hasCachedAdmin) {
    return <>{children}</>
  }

  console.log('🔒 [ProtectedAdminRoute] Estado atual:', { user, loading })

  // Timeout de segurança para evitar loading infinito (mais tolerante)
  React.useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => {
        console.log('⚠️ [ProtectedAdminRoute] Timeout de segurança atingido')
        setTimeoutReached(true)
      }, 20000) // 20 segundos

      return () => clearTimeout(timer)
    }
  }, [loading])

  // Se o timeout foi atingido, mostrar um estado de fallback sem redirecionar imediatamente
  if (timeoutReached && loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-admin-50 to-admin-100">
        <div className="text-center p-8 bg-white rounded-2xl shadow-xl">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-admin-600 border-t-transparent mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Conexão lenta</h2>
          <p className="text-gray-600">Ainda verificando permissões de administrador...</p>
        </div>
      </div>
    )
  }

  // Se ainda está carregando a autenticação, mostrar loading
  if (loading) {
    console.log('⏳ [ProtectedAdminRoute] Mostrando loading...')
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-admin-50 to-admin-100">
        <div className="text-center p-8 bg-white rounded-2xl shadow-xl">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-admin-600 border-t-transparent mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Carregando...</h2>
          <p className="text-gray-600">Verificando suas permissões de administrador</p>
          <div className="mt-4 flex justify-center space-x-2">
            <div className="w-2 h-2 bg-admin-600 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-admin-600 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
            <div className="w-2 h-2 bg-admin-600 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
          </div>
        </div>
      </div>
    )
  }

  // Se não há usuário autenticado ou não é admin, redirecionar para login
  if ((!user || !user.admin) && !hasCachedAdmin) {
    return <Navigate to="login" replace />
  }

  // Usuário autenticado e com permissão de admin, permitir acesso
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {children}
    </motion.div>
  )
}

export default ProtectedAdminRoute
